/**
 * @file promotion-utils.js
 * @description Pure utility functions for the promotion system
 * @module GniibPromotions.Utils
 *
 * Contains formatting, DOM helpers, and variant ID extraction utilities.
 * All functions are stateless and have no side effects.
 */
(function(global) {
  'use strict';

  var GP = global.GniibPromotions = global.GniibPromotions || {};

  /**
   * Utility functions
   * @namespace GniibPromotions.Utils
   */
  GP.Utils = {

    /**
     * Format price from cents to dollars with 2 decimal places
     * @param {number} priceInCents - Price in cents (e.g., 10000 for $100.00)
     * @returns {string} Formatted price (e.g., "100.00")
     */
    formatPrice: function(priceInCents) {
      return (priceInCents / 100).toFixed(2);
    },

    /**
     * Format special price (already in dollars, not cents)
     * @param {number} price - Price in dollars
     * @returns {string} Formatted price with 2 decimal places
     */
    formatSpecialPrice: function(price) {
      return parseFloat(price).toFixed(2);
    },

    /**
     * Extract numeric variant ID from GID format
     * Converts "gid://shopify/ProductVariant/123456" to 123456
     * @param {string|number} variantId - Variant ID (GID string or number)
     * @returns {number} Numeric variant ID
     */
    extractVariantId: function(variantId) {
      if (typeof variantId === 'string' && variantId.includes('gid://')) {
        return parseInt(variantId.split('/').pop(), 10);
      }
      return parseInt(variantId, 10);
    },

    /**
     * Get DOM elements by their IDs from config
     * @returns {Object} Object with DOM element references
     */
    getDomElements: function() {
      var ids = GP.DomIds;
      var elements = {};

      // Map each ID to its element
      for (var key in ids) {
        if (ids.hasOwnProperty(key)) {
          elements[key] = document.getElementById(ids[key]);
        }
      }

      return elements;
    },

    /**
     * Get current page language
     * @returns {string} Language code ('en', 'zh-TW', or 'zh-CN')
     */
    getCurrentLanguage: function() {
      var lang = document.documentElement.lang || 'zh-TW';
      if (lang.startsWith('en')) return 'en';
      if (lang === 'zh-CN' || lang === 'zh-Hans') return 'zh-CN';
      return 'zh-TW';
    },

    /**
     * Create return URL for login/register buttons
     * @returns {string} Encoded return URL
     */
    getReturnUrl: function() {
      return encodeURIComponent(window.location.pathname + window.location.search);
    },

    /**
     * Setup login/register button hrefs with return_to parameter
     * @param {HTMLElement} loginBtn - Login button element
     * @param {HTMLElement} registerBtn - Register button element
     */
    setupAuthButtons: function(loginBtn, registerBtn) {
      var returnUrl = this.getReturnUrl();
      if (loginBtn && loginBtn.href) {
        loginBtn.href = loginBtn.href + '?return_to=' + returnUrl;
      }
      if (registerBtn && registerBtn.href) {
        registerBtn.href = registerBtn.href + '?return_to=' + returnUrl;
      }
    },

    /**
     * Create composite key for variant tracking
     * @param {Object} product - Product object with handle
     * @param {Object} variant - Variant object with title
     * @returns {string} Composite key in format "handle-title"
     */
    createVariantKey: function(product, variant) {
      return product.handle + '-' + variant.title;
    },

    /**
     * Create item key for selection Map
     * @param {string|number} productId - Product ID
     * @param {string|number} variantId - Variant ID
     * @returns {string} Item key in format "productId-variantId"
     */
    createItemKey: function(productId, variantId) {
      return productId + '-' + variantId;
    },

    /**
     * Check if role allows liuyang URL parameter override
     * @param {string} urlRole - Role from URL parameter
     * @returns {boolean} True if URL role is allowed
     */
    isUrlRoleAllowed: function(urlRole) {
      // Only liuyang promotion allows URL parameter role
      return urlRole && urlRole.toLowerCase() === 'liuyang';
    },

    /**
     * Debounce function execution
     * @param {Function} func - Function to debounce
     * @param {number} wait - Wait time in milliseconds
     * @returns {Function} Debounced function
     */
    debounce: function(func, wait) {
      var timeout;
      return function() {
        var context = this;
        var args = arguments;
        clearTimeout(timeout);
        timeout = setTimeout(function() {
          func.apply(context, args);
        }, wait);
      };
    },

    /**
     * Deep clone an object
     * @param {Object} obj - Object to clone
     * @returns {Object} Cloned object
     */
    clone: function(obj) {
      return JSON.parse(JSON.stringify(obj));
    },

    /**
     * Check if a value is empty (null, undefined, empty string, empty array)
     * @param {*} value - Value to check
     * @returns {boolean} True if empty
     */
    isEmpty: function(value) {
      if (value === null || value === undefined) return true;
      if (typeof value === 'string' && value.trim() === '') return true;
      if (Array.isArray(value) && value.length === 0) return true;
      return false;
    },

    /**
     * Safe parseInt with fallback
     * @param {*} value - Value to parse
     * @param {number} fallback - Fallback value if parsing fails
     * @returns {number} Parsed integer or fallback
     */
    safeParseInt: function(value, fallback) {
      var parsed = parseInt(value, 10);
      return isNaN(parsed) ? (fallback || 0) : parsed;
    },

    /**
     * Format date for display
     * @param {string|Date} date - Date to format
     * @returns {string} Formatted date string
     */
    formatDate: function(date) {
      return new Date(date).toLocaleDateString('zh-TW');
    },

    /**
     * Calculate percentage off from discount rate
     * @param {number} discountRate - Discount rate (e.g., 0.2 for 20% off)
     * @returns {number} Percentage value (e.g., 20)
     */
    getDiscountPercent: function(discountRate) {
      return Math.round(discountRate * 100);
    },

    /**
     * Calculate discounted price
     * @param {number} originalPrice - Original price
     * @param {number} discountRate - Discount rate (e.g., 0.2 for 20% off)
     * @returns {number} Discounted price
     */
    applyDiscount: function(originalPrice, discountRate) {
      return originalPrice * (1 - discountRate);
    },

    /**
     * Calculate price after percentage discount
     * @param {number} originalPrice - Original price
     * @param {number} discountPercentage - Discount percentage (e.g., 30 for 30% off)
     * @returns {number} Discounted price
     */
    applyPercentageDiscount: function(originalPrice, discountPercentage) {
      return originalPrice * (1 - discountPercentage / 100);
    }
  };

})(window);
